import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarHeart, UtensilsCrossed, Users } from "lucide-react";
import { canonical } from "@/lib/seo";

export const Route = createFileRoute("/meetings-and-events")({
  head: () => ({
    meta: [
      { title: "Meetings & Events - Malak BnB Resort" },
      { name: "description", content: "Host unforgettable weddings, celebrations, and corporate meetings at Malak BnB Resort in Crystal Beach." },
      { property: "og:title", content: "Meetings & Events - Malak BnB Resort" },
      { property: "og:description", content: "Elegant venue for intimate gatherings up to 25 guests." },
      { property: "og:url", content: "https://malakbnbresort.ca/meetings-and-events" },
      { property: "og:image", content: "/images/events-setup.webp" },
    ],
    links: [canonical("/meetings-and-events")],
  }),
  component: EventsPage,
});

const IMAGES = {
  hero: "/images/events-setup.webp",
  food: "/images/events-food.webp",
  hallway: "/images/events-hallway.webp",
  stairway: "/images/events-stairway.webp",
};

function EventsPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] overflow-hidden">
        <img src={IMAGES.hero} alt="Event setup at Malak BnB Resort" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <h1 className="text-4xl sm:text-5xl font-heading text-white animate-fade-in-up">Meetings & Events</h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading text-foreground">Create Unforgettable Moments</h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            At Malak BnB Resort, we specialize in creating unforgettable experiences for your special events.
            Nestled in the serene beauty of Crystal Beach, our elegant venue is ideal for intimate gatherings
            of up to 25 guests. Whether you're planning a wedding, birthday celebration, graduation, or
            corporate meeting, our dedicated team is here to bring your vision to life.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Our unique location blends the warmth of home-like hospitality with the sophistication of a
            five-star resort, ensuring that every aspect of your event is meticulously planned.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Event Planning */}
            <div className="rounded-2xl bg-background p-8 shadow-sm hover:shadow-lg transition-shadow">
              <CalendarHeart className="h-10 w-10 text-primary mb-5" />
              <h3 className="font-heading text-xl text-foreground mb-3">Event Planning</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                With extensive experience in event planning, our team offers unparalleled service for
                small group events. From birthdays and weddings to graduations and social gatherings,
                we ensure every detail is flawlessly executed.
              </p>
              <Link to="/contact" className="mt-4 inline-flex text-primary font-semibold text-sm hover:underline">
                Start Planning →
              </Link>
            </div>

            {/* Weddings */}
            <div className="rounded-2xl bg-background p-8 shadow-sm hover:shadow-lg transition-shadow">
              <Users className="h-10 w-10 text-primary mb-5" />
              <h3 className="font-heading text-xl text-foreground mb-3">Weddings</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                A small wedding of up to 25 guests is the perfect way to celebrate at our resort.
                Host your ceremony in our beautiful garden under a stylish tent, next to the pool,
                followed by an elegant dining experience with luxurious accommodations for up to 8 guests.
              </p>
              <Link to="/contact" className="mt-4 inline-flex text-primary font-semibold text-sm hover:underline">
                Request a Proposal →
              </Link>
            </div>

            {/* Catering */}
            <div className="rounded-2xl bg-background p-8 shadow-sm hover:shadow-lg transition-shadow">
              <UtensilsCrossed className="h-10 w-10 text-primary mb-5" />
              <h3 className="font-heading text-xl text-foreground mb-3">Catering</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our experienced culinary team brings a flair for kosher, Middle Eastern, and
                Italian-inspired cuisine, all prepared with the freshest ingredients. We pair our
                dishes with the best wines Niagara has to offer.
              </p>
              <a
                href="https://www.malakbnbresort.ca/_files/ugd/03d66f_c8f1ba5715da4f3abcf72025e70a2ded.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex text-primary font-semibold text-sm hover:underline"
              >
                Download Our Menu →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-muted-foreground text-sm mb-2">
            Planning something smaller, like a birthday or an anniversary?
          </p>
          <Link to="/small-events-crystal-beach" className="text-primary font-semibold text-sm hover:underline">
            See small events and private gatherings →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-3xl font-heading text-primary-foreground">Let Us Plan Your Event</h2>
          <p className="mt-4 text-primary-foreground/80">
            Contact us to discuss your event vision and let our team create a tailored proposal.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-white text-primary px-10 py-4 text-sm font-semibold hover:bg-white/90 transition-colors shadow-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
